import type React from 'react';

/**
 * Robust "click the backdrop to close" behaviour for modals.
 *
 * A naive `onClick={onClose}` (or even one guarded by
 * `e.target === e.currentTarget`) closes the modal in a very common yet
 * surprising case: the user drags to select text inside an input, then
 * releases the mouse button slightly outside the modal. The browser fires the
 * resulting `click` on the nearest common ancestor of the mousedown and mouseup
 * targets — the backdrop — so the modal closes and everything typed so far is
 * lost.
 *
 * To close only on a genuine backdrop click we require BOTH the press
 * (mousedown) and the release (click) to land directly on the backdrop element.
 * A press that starts inside the modal content can never close it, no matter
 * where the mouse is released.
 *
 * Spread the returned props onto the backdrop element:
 *
 *   <div className="fixed inset-0 ..." {...backdropClose(onClose)}>
 *     <div onClick={e => e.stopPropagation()}>...</div>
 *   </div>
 *
 * Mouse interactions are strictly sequential (a single pointer, one press at a
 * time), so a shared module-level flag is safe even with stacked modals.
 */
let pressStartedOnBackdrop = false;

export function backdropClose(onClose: () => void) {
    return {
        onMouseDown: (e: React.MouseEvent) => {
            pressStartedOnBackdrop = e.target === e.currentTarget;
        },
        onClick: (e: React.MouseEvent) => {
            if (e.target === e.currentTarget && pressStartedOnBackdrop) {
                onClose();
            }
            pressStartedOnBackdrop = false;
        },
    };
}
